import { useEffect, useMemo } from "react";
import { useForm } from "react-hook-form";

import { yupResolver } from "@hookform/resolvers/yup";
import { Timezone, US_MAJOR_CITY_TIMEZONES } from "@m7-health/shared-utils";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { object, string } from "yup";

import { Box, Grid, TextField, Typography } from "@mui/material";

import { useAsyncInvalidateQuery } from "~/api/_shared/hooks";
import {
  IUnit,
  Unit,
  useArchiveUnitMutation,
  useRestoreUnitMutation,
  useUpdateUnitMutation,
} from "~/api/unit";
import { createUnitApi } from "~/routes/api";
import { ICreateUnit } from "~/routes/api/types";

import { EHierarchyModelViewType, HierarchyModelBase } from "#/features/Hierarchy/types";
import { useListSchedulesQuery } from "@/api";
import { Autocomplete, CustomSelectControlled } from "@/common/components";
import { CustomInputControlled } from "@/common/components/Input";
import CustomModal from "@/common/components/Modal";
import { ICustomSelectItem } from "@/common/components/TrackedComponents/Select/types";
import { NOT_EXISTING_UUID } from "@/common/constants";
import { useCurrentRole } from "@/common/hooks";
import { useErrors } from "@/common/hooks/useErrors";
import { useToast } from "@/common/hooks/useToast";
import { getMaxMsg, getMaxNumber, requiredMsg } from "@/common/validation/messages";
import { integerShapeRequired } from "@/common/validation/shapes";

import { ArchiveAtForm } from "./ArchiveAtForm";

const UnitFormModal = ({
  selected,
  onComplete,
  parentOptions = [],
}: {
  selected?: HierarchyModelBase;
  onComplete: (updated?: boolean) => void;
  parentOptions?: HierarchyModelBase[];
}) => {
  const isAdmin = useCurrentRole().userIsAdmin;
  const asyncInvalidateQuery = useAsyncInvalidateQuery();
  const { showSuccess } = useToast();
  const { handleErrors } = useErrors();
  const queryClient = useQueryClient();
  const parentItems: ICustomSelectItem<HierarchyModelBase>[] = useMemo(() => {
    return parentOptions?.map((option) => ({
      label: option.name,
      value: option.id,
      item: option,
    }));
  }, [parentOptions]);

  const id = selected?.id;

  const formSchema = {
    scheduleDurationInWeeks: integerShapeRequired.max(12, getMaxNumber(12)),
    unitName: string().max(50, getMaxMsg(50)).required(requiredMsg),
    ...(!selected?.id ? { unitTimezone: string().required() } : {}),
  };

  const {
    control,
    reset,
    formState: { errors, isValid, isDirty },
    watch,
    getValues,
    setValue,
    setError,
  } = useForm({
    mode: "onChange",
    resolver: yupResolver(object().shape(formSchema).required()),
  });

  useEffect(() => {
    if (selected?.id) {
      reset({
        scheduleDurationInWeeks: selected?.scheduleDurationInWeeks,
        unitName: selected?.name,
        unitTimezone: null,
        unitFacility: selected?.facilityId,
      });
    } else {
      reset({
        scheduleDurationInWeeks: 1,
        unitName: "",
        unitTimezone: null,
        unitFacility: selected?.facilityId,
      });
    }
  }, [selected, reset]);
  const { data: unitSchedules } = useListSchedulesQuery({
    unitIds: [selected?.id || NOT_EXISTING_UUID],
  });
  const schedulesCount = unitSchedules?.length || 0;

  const onError = (error: unknown) => handleErrors(error, setError);

  const onSettled = () => {
    void queryClient.invalidateQueries({ queryKey: ["appConfig"] });
  };

  const { mutateAsync: archiveMutation } = useArchiveUnitMutation({});
  const { mutateAsync: restoreMutation } = useRestoreUnitMutation({});

  const { mutate: createMutation, isPending: createApiLoading } = useMutation({
    mutationFn: createUnitApi,

    onSuccess: () => {
      reset();
      showSuccess("New unit created successfully");
      onComplete(true);
    },
    onError,
    onSettled,
  });

  const { mutate: updateMutation, isPending: editApiLoading } = useUpdateUnitMutation({
    onSuccess: () => {
      if (id) {
        reset();
        showSuccess("Unit updated successfully");
        onComplete(true);
      }
    },
    onError,
    onSettled,
  });

  const currentUnitTimezone = watch("unitTimezone") as Timezone | undefined;
  const currentUnitTimezoneOption = useMemo(
    () =>
      currentUnitTimezone
        ? US_MAJOR_CITY_TIMEZONES.find((option) => option.timezone === currentUnitTimezone)
        : undefined,
    [currentUnitTimezone],
  );

  const handleUpdate = () => {
    const { scheduleDurationInWeeks, unitName, unitTimezone, unitFacility } = getValues() as {
      scheduleDurationInWeeks: number;
      unitName: string;
      unitTimezone: Timezone;
      unitFacility: IUnit["facilityId"];
    };

    const payload = {
      scheduleDurationInWeeks,
      unitTimezone,
      name: unitName,
      unitName,
      facilityId: unitFacility,
    };

    if (selected?.id) {
      updateMutation({ id: selected?.id, ...payload } as Unit.API.Update.QueryParams);
    } else {
      createMutation(payload as ICreateUnit);
    }
  };

  const onSecondaryBtnClick = () => {
    reset();
    onComplete();
  };

  const isLoading = editApiLoading || createApiLoading;

  const modalContent = (
    <Grid container flexDirection="column" data-testid={"unit-modal-content"}>
      <Grid item mb={2}>
        <Typography fontSize="0.875rem" sx={{ opacity: 0.6 }}>
          * Required information
        </Typography>
      </Grid>
      <Grid item>
        <CustomInputControlled
          label="Unit name*"
          name="unitName"
          control={control}
          errors={errors}
          fullWidth
        />
      </Grid>
      <Grid item mt={2}>
        <CustomSelectControlled
          items={parentItems}
          label="Select unit facility*"
          name="unitFacility"
          control={control}
          fullWidth
        />
      </Grid>

      <Grid item mt={2}>
        <Autocomplete<(typeof US_MAJOR_CITY_TIMEZONES)[number], true>
          multiple
          trackingLabel="unitTimezone"
          options={US_MAJOR_CITY_TIMEZONES}
          groupBy={(option) => option.state}
          getOptionLabel={(option) => `${option.city} (${option.state}) - ${option.timezone}`}
          // If there are schedules, heavy manual work is required to change
          //  timestamps of schedules and shifts. CX Need to contact dev team
          disabled={!!selected?.id && schedulesCount > 0}
          size="small"
          value={currentUnitTimezoneOption ? [currentUnitTimezoneOption] : []}
          onChange={(_event, [oldOption, newOption]) => {
            const timezoneToSet = newOption?.timezone || oldOption?.timezone;
            if (!timezoneToSet) return;

            setValue("unitTimezone", timezoneToSet, { shouldDirty: true, shouldValidate: true });
          }}
          renderTags={([option]) => <Box ml={1}>{option?.timezone}</Box>}
          renderInput={(params) => <TextField {...params} label="Timezone" />}
          noOptionsText="City is not listed. Please check the correct timezone on Google and select it from the dropdown."
        />
      </Grid>

      <Grid item mt={2}>
        <CustomInputControlled
          control={control}
          errors={errors}
          label="Unit schedule duration (weeks)*"
          name="scheduleDurationInWeeks"
          type="number"
          counter
          maxNumber={12}
        />
      </Grid>

      {isAdmin && selected?.id && (
        <ArchiveAtForm
          type={EHierarchyModelViewType.unit}
          selected={selected}
          sx={{ position: "absolute", bottom: 32, right: 32 }}
          handleDelete={(_event, setOpen) => {
            void (async () => {
              try {
                await archiveMutation({ id: selected.id ?? "" });
                showSuccess("Unit archived successfully");
                setOpen(false);
                await asyncInvalidateQuery(null);
                onComplete(true);
              } catch (error) {
                onError(error);
              }
            })();
          }}
          handleRestore={(_event, setOpen) => {
            void (async () => {
              try {
                await restoreMutation({ id: selected.id ?? "" });
                showSuccess("Unit restored successfully");
                setOpen(false);
                await asyncInvalidateQuery(null);
                onComplete(true);
              } catch (error) {
                onError(error);
              }
            })();
          }}
        />
      )}
    </Grid>
  );

  return (
    <CustomModal
      isOpen={!!selected}
      modalContent={modalContent}
      modalHeaderText={selected?.id ? "Edit Unit" : "Create a new unit"}
      primaryBtnText={selected?.id ? "Save changes" : "Create"}
      onSecondaryBtnClick={onSecondaryBtnClick}
      onSubmit={handleUpdate}
      primaryDisabled={!isValid || isLoading || !isDirty}
    />
  );
};

export default UnitFormModal;
